import React, { useState } from "react";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useBulkUploadDocuments } from "../../../../backend/compliance";
import { ComplianceActivityStatus } from "../../../../constants/Compliance.constants";
import { toBackendDateFormat } from "../../../../utils/common";
import {
    validateFileWithContent,
    FILE_TYPE_GROUPS,
    FILE_SIZE_LIMITS,
    generateAcceptAttribute,
    VALIDATION_MESSAGES,
} from "../../../../utils/fileValidation";
import './ComplianceBulkModals.css';

const UPLOADABLE_STATUSES = [ComplianceActivityStatus.DUE, ComplianceActivityStatus.NON_COMPLIANT];
const TODAY = dayjs().format('YYYY-MM-DD');

/* ── Inline SVG icons (no external dependency) ─── */
function IconCloudUpload() {
    return (
        <svg width="31" height="31" viewBox="0 0 24 24" fill="none" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 16 12 12 8 16"/>
            <line x1="12" y1="12" x2="12" y2="21"/>
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
        </svg>
    );
}
function IconDownload() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2965ad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
    );
}
function IconTrash() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e53e3e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            <path d="M10 11v6M14 11v6"/>
            <path d="M9 6V4h6v2"/>
        </svg>
    );
}
function IconClose() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
    );
}
function IconInfo() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
    );
}
function IconWarn() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
    );
}

function ComplianceBulkUploadModal({ onClose, selectedItems }: any) {
    const [uploadFiles, setUploadFiles]           = useState<any[]>([]);
    const [auditedDate, setAuditedDate]           = useState('');
    const [formsStatusRemarks, setFormsStatusRemarks] = useState('');
    const [submitToManager, setSubmitToManager]   = useState(false);

    const { bulkUploadDocuments, uploading } = useBulkUploadDocuments(
        function(data: any) {
            // `post()` returns the full Axios response; the JSON body is at .data.
            // Handle both shapes defensively.
            var body = (data && data.data && typeof data.data === 'object') ? data.data : (data || {});
            // New API shape: { uploaded[], skipped[], failed[], summary: { uploaded_count, skipped_count, failed_count, message } }
            // Legacy shape (no summary): treat entire upload as success.
            onClose(true);

            // ── Resolve which actions were actually performed ─────────────────
            // Used to pick the right success copy below.
            var hadFields = Boolean(auditedDate) || Boolean(formsStatusRemarks.trim());

            var summary = body.summary;
            if (summary) {
                var uploadedCount = summary.uploaded_count || 0;
                var skippedCount  = summary.skipped_count  || 0;
                var failedCount   = summary.failed_count   || 0;
                var hadFiles      = uploadedCount > 0;

                // 1. Success toast — copy varies by what the user actually did
                // Priority: Submit to Manager > Files + Fields > Files only > Fields only
                if (submitToManager) {
                    toast.success("Activities submitted to Manager successfully.");
                } else if (hadFiles && hadFields) {
                    toast.success("Files uploaded and compliance details updated successfully.");
                } else if (hadFiles) {
                    toast.success("Files uploaded successfully to Upcoming and Non-Compliant Activities.");
                } else if (hadFields) {
                    toast.success("Compliance details updated successfully.");
                }

                // 2. Warning toast — files skipped due to security validation
                if (skippedCount > 0) {
                    toast.warning(
                        summary.message ||
                        skippedCount + " file(s) were skipped due to security validation."
                    );
                }

                // 3. Error toast — files that failed for other reasons (DB / upload errors)
                if (failedCount > 0) {
                    toast.error(failedCount + " file(s) failed to upload. Please try again.");
                    var failedFiles = body.failed || [];
                    failedFiles.forEach(function(f: any) {
                        var errs = (f.errors || []).join(', ');
                        toast.error(f.file_name + (errs ? ': ' + errs : ''));
                    });
                }
            } else {
                // Legacy response (no summary field) — derive from form state
                var hadFilesL = uploadFiles.length > 0;
                if (submitToManager) {
                    toast.success("Activities submitted to Manager successfully.");
                } else if (hadFilesL && hadFields) {
                    toast.success("Files uploaded and compliance details updated successfully.");
                } else if (hadFilesL) {
                    toast.success("Files uploaded successfully to Upcoming and Non-Compliant Activities.");
                } else {
                    toast.success("Compliance details updated successfully.");
                }
            }
        },
        function(error: any) {
            var data = (error && error.response && error.response.data) || {};
            toast.error(data.error || data.message || "File upload failed.");
            var details = data.details;
            if (details && details.length > 0) {
                details.forEach(function(detail: string) { toast.error(detail); });
            }
        }
    );

    async function onFileChange(event: any) {
        var _uploadedFiles = uploadFiles.slice();
        if (event) {
            var length = event.target.files.length;
            var time = new Date().getTime();
            for (var i = 0; i < length; i++) {
                var file = event.target.files[i];
                var validation = await validateFileWithContent(
                    file,
                    FILE_TYPE_GROUPS.COMPLIANCE_DOCUMENTS,
                    FILE_SIZE_LIMITS.LARGE
                );
                _uploadedFiles.push({
                    id: time + i,
                    file: file,
                    invalidFile: !validation.isValid,
                    errorMessage: validation.error,
                });
            }
            setUploadFiles(_uploadedFiles);
            event.target.value = null;
        }
    }

    function onDeleteFile(index: number) {
        var _uploadedFiles = uploadFiles.slice();
        _uploadedFiles.splice(index, 1);
        setUploadFiles(_uploadedFiles);
    }

    function downloadFile(file: File) {
        var url = URL.createObjectURL(file);
        var a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
    }

    function clearAll() {
        setUploadFiles([]);
        setAuditedDate('');
        setFormsStatusRemarks('');
        setSubmitToManager(false);
    }

    function handleSubmit() {
        var eligibleItems = (selectedItems || []).filter(function(item: any) {
            return UPLOADABLE_STATUSES.indexOf(item.status) !== -1;
        });
        if (eligibleItems.length === 0) {
            toast.warning("None of the selected activities are in Upcoming or Non-Compliant status.");
            return;
        }
        if (submitToManager && (!auditedDate || !formsStatusRemarks.trim())) {
            toast.warning("Actual Completion Date and Comments are required to submit to Compliance Manager.");
            return;
        }
        var formData = new FormData();
        uploadFiles.forEach(function(f) { formData.append("files[]", f.file); });
        eligibleItems.forEach(function(item: any) { formData.append("compliance_ids[]", item.id); });
        if (auditedDate) {
            formData.append("audited_date", toBackendDateFormat(new Date(auditedDate)) as string);
        }
        if (formsStatusRemarks.trim()) {
            formData.append("forms_status_remarks", formsStatusRemarks.trim());
        }
        formData.append("is_to_be_submitted", String(submitToManager));
        bulkUploadDocuments(formData);
    }

    var hasInvalidFile         = uploadFiles.some(function(f) { return f.invalidFile; });
    var hasAnyAction           = uploadFiles.length > 0 || Boolean(auditedDate) || Boolean(formsStatusRemarks.trim()) || submitToManager;
    var missingReqForSubmit    = submitToManager && (!auditedDate || !formsStatusRemarks.trim());

    return (
        <>
            <div className="cbm-overlay">
                <div className="cbm-modal">

                    {/* Header */}
                    <div className="cbm-header">
                        <h2 className="cbm-title">Bulk Upload</h2>
                        <button className="cbm-close" onClick={function() { onClose(); }} title="Close">
                            <IconClose />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="cbm-body">

                        {/* File upload zone */}
                        <div className="cbm-field">
                            <label className="cbm-label">Select files to upload</label>
                            <div className="cbm-upload-zone">
                                <input
                                    type="file"
                                    onChange={onFileChange}
                                    accept={generateAcceptAttribute(FILE_TYPE_GROUPS.COMPLIANCE_DOCUMENTS)}
                                    multiple
                                />
                                <div className="cbm-upload-icon">
                                    <IconCloudUpload />
                                </div>
                                <p className="cbm-upload-text">Drag and drop files here or click to browse</p>
                                <div className="cbm-upload-btn">Choose File</div>
                                <p className="cbm-upload-hint">{VALIDATION_MESSAGES.ALLOWED_FILE_COMPLIANCE_DOCS}</p>
                            </div>
                        </div>

                        {/* File list */}
                        {uploadFiles.length > 0 && (
                            <div className="cbm-field">
                                <div className="cbm-file-list">
                                    {uploadFiles.map(function(file, index) {
                                        return (
                                            <div
                                                className={'cbm-file-row' + (file.invalidFile ? ' cbm-file-row-invalid' : '') + (uploading ? ' cbm-file-row-uploading' : '')}
                                                key={file.id}
                                            >
                                                <div className="cbm-file-name-wrap">
                                                    <div className={'cbm-file-name' + (file.invalidFile ? ' cbm-file-bad' : '') + (uploading ? ' cbm-file-name-pending' : '')}>
                                                        {file.file.name}
                                                    </div>
                                                    {file.invalidFile && !uploading && (
                                                        <div className="cbm-file-err">
                                                            {file.errorMessage || VALIDATION_MESSAGES.INVALID_FILE_COMPLIANCE_DOCS}
                                                        </div>
                                                    )}
                                                </div>
                                                {uploading ? (
                                                    <div className="cbm-file-actions">
                                                        <span className="cbm-file-uploading-label">Uploading…</span>
                                                        <svg className="cbm-file-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2965ad" strokeWidth="2.5" strokeLinecap="round">
                                                            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                                                            <path d="M12 2a10 10 0 0 1 10 10" />
                                                        </svg>
                                                    </div>
                                                ) : (
                                                    <div className="cbm-file-actions">
                                                        <button
                                                            className="cbm-file-dl"
                                                            onClick={function() { downloadFile(file.file); }}
                                                            title="Download"
                                                        >
                                                            <IconDownload />
                                                        </button>
                                                        <button
                                                            className="cbm-file-del"
                                                            onClick={function() { onDeleteFile(index); }}
                                                            title="Remove"
                                                        >
                                                            <IconTrash />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Actual Completion Date */}
                        <div className="cbm-field">
                            <label className="cbm-label">
                                Actual Completion Date
                                {submitToManager && <span className="cbm-required">*</span>}
                            </label>
                            <input
                                type="date"
                                className="cbm-input"
                                max={TODAY}
                                value={auditedDate}
                                onChange={function(e) { setAuditedDate(e.target.value); }}
                            />
                        </div>

                        {/* Comments */}
                        <div className="cbm-field">
                            <label className="cbm-label">
                                Comments for Manager
                                {submitToManager && <span className="cbm-required">*</span>}
                            </label>
                            <textarea
                                className="cbm-textarea"
                                rows={3}
                                placeholder="Enter your comments here..."
                                value={formsStatusRemarks}
                                onChange={function(e) { setFormsStatusRemarks(e.target.value); }}
                            />
                        </div>

                        {/* Submit to Manager toggle */}
                        <div className="cbm-field">
                            <div className="cbm-check-block">
                                <label className="cbm-check-row">
                                    <input
                                        type="checkbox"
                                        className="cbm-check-input"
                                        checked={submitToManager}
                                        onChange={function(e) { setSubmitToManager(e.target.checked); }}
                                    />
                                    <div>
                                        <div className="cbm-check-text">Submit to Compliance Manager</div>
                                        <div className="cbm-check-desc">
                                            When checked, marks selected activities as Approval Pending for manager review.
                                            Actual Completion Date and Comments become mandatory.
                                        </div>
                                    </div>
                                </label>
                                {missingReqForSubmit && (
                                    <div className="cbm-check-warning">
                                        <IconWarn />
                                        Please fill Actual Completion Date and Comments to submit.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Notice */}
                        <div className="cbm-notice">
                            <IconInfo />
                            Only activities in <strong>&nbsp;Upcoming&nbsp;</strong> or <strong>&nbsp;Non-Compliant&nbsp;</strong> status will be updated.
                        </div>

                    </div>

                    {/* Footer */}
                    <div className="cbm-footer">
                        <button className="cbm-btn cbm-btn-back" disabled={uploading} onClick={function() { onClose(); }}>
                            Back
                        </button>
                        <div className="cbm-footer-right">
                            <button className="cbm-btn cbm-btn-reset" disabled={uploading} onClick={clearAll}>
                                Reset
                            </button>
                            <button
                                className="cbm-btn cbm-btn-primary"
                                onClick={handleSubmit}
                                disabled={uploading || !hasAnyAction || hasInvalidFile || missingReqForSubmit}
                            >
                                {uploading ? 'Uploading…' : 'Submit'}
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
}

export default ComplianceBulkUploadModal;
